import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SvgIcon from '@mui/material/SvgIcon';
import {
  cancelarNfceVenda,
  cancelSale,
  downloadNfceVendaPdf,
  enviarWhatsAppVenda,
  reemitirNfceVenda,
  getConvenioReport,
  getCashbackReport,
  getPaymentMethodsReport,
  getSalesBySellerReport,
  getCommissionBySellerReport,
  alterarFormasPagamentoVenda,
  getAllPaymentMethods,
  getProductsReport,
  getProductsBySellerReport,
  getSaleReportDetail,
  getSalesReport,
  getAllUsers,
  getAllPdvs,
  getAllCategories,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../utils/reportBranding';
import { toDateInputValue } from '../utils/datetimeLocal';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';

const canCancelSale = () => {
  try {
    const permissoes = getAuthPermissoes();
    if (!permissoes) return false;
    return Boolean(permissoes.cancelaVenda || permissoes.CancelaVenda);
  } catch {
    return false;
  }
};

const canAlterarFormaPagamento = () => {
  try {
    const permissoes = getAuthPermissoes();
    if (!permissoes) return false;
    return Boolean(permissoes.alteraFormaPagamento || permissoes.AlteraFormaPagamento);
  } catch {
    return false;
  }
};

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

function WhatsAppIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2m.01 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.54-3.7 8.24-8.23 8.24m4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.10-.23-.17-.48-.29"
      />
    </SvgIcon>
  );
}

const digitsPhone = (v) => String(v || '').replace(/\D/g, '').slice(0, 11);

const formatPhone = (v) => {
  const d = digitsPhone(v);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const localDate = toDateInputValue;

const initialFilters = (params) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const categoriaIds = params?.getAll?.('categoriaIds')?.filter(Boolean)
    || (params?.get('categoriaIds') ? [params.get('categoriaIds')] : []);
  return {
    de: params?.get('de') || localDate(first),
    ate: params?.get('ate') || localDate(now),
    status: params?.get('status') || '',
    pdvId: params?.get('pdvId') || '',
    usuarioId: params?.get('usuarioId') || '',
    vendedorId: params?.get('vendedorId') || '',
    busca: params?.get('busca') || '',
    categoriaIds,
  };
};

const tabFromParams = (params) => {
  const raw = Number(params?.get('tab'));
  return Number.isFinite(raw) && raw >= 0 && raw <= 7 ? raw : 0;
};

const csvEscape = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;

const downloadCsv = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(';'),
    ...rows.map((row) => row.map(csvEscape).join(';')),
  ].join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const decodePdfBase64 = (raw) => {
  if (!raw) return null;
  let text = String(raw).trim();
  const marker = 'base64,';
  const at = text.indexOf(marker);
  if (at >= 0) text = text.slice(at + marker.length);
  try {
    const bin = atob(text.replace(/\s/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    if (bytes.length > 4 && bytes[0] === 0x25 && bytes[1] === 0x50) return bytes;
  } catch {
    return null;
  }
  return null;
};

const printPdfBytes = (bytes, filename = 'DANFE-NFCe.pdf') => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, '_blank', 'width=720,height=900');
  if (!popup) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    window.alert('O navegador bloqueou a janela. O PDF da NFC-e foi baixado.');
    return;
  }
  window.setTimeout(() => {
    try {
      popup.focus();
      popup.print();
    } catch {
      /* o visualizador nativo do PDF imprime pelo próprio diálogo */
    }
  }, 600);
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
};

const printSale = async (sale, company = {}) => {
  if (!sale) return;
  const st = String(sale.statusFiscal || '').toLowerCase();
  if (st === 'fiscal_rejeitada') {
    window.alert(
      `NFC-e rejeitada. Não é possível imprimir comprovante não fiscal.\n\n${
        sale.mensagemFiscal || 'Sem detalhe da SEFAZ.'
      }`
    );
    return;
  }
  if (st === 'fiscal_pendente' || st === 'processando') {
    window.alert(
      `NFC-e ainda não autorizada. Aguarde a autorização para imprimir o cupom fiscal.\n\n${
        sale.mensagemFiscal || ''
      }`.trim()
    );
    return;
  }
  const fiscal = st === 'autorizada';
  let pdfBytes = fiscal ? decodePdfBase64(sale.pdf) : null;
  if (fiscal && !pdfBytes && sale.id) {
    try {
      const { data } = await downloadNfceVendaPdf(sale.id);
      const buf = data instanceof Blob ? await data.arrayBuffer() : data;
      const bytes = new Uint8Array(buf);
      if (bytes.length > 4 && bytes[0] === 0x25 && bytes[1] === 0x50) pdfBytes = bytes;
    } catch {
      pdfBytes = null;
    }
  }
  if (pdfBytes) {
    printPdfBytes(pdfBytes, `DANFE-NFCe-${sale.id}.pdf`);
    return;
  }

  const popup = window.open('', '_blank', 'width=520,height=760');
  if (!popup) {
    window.alert('O navegador bloqueou a janela de impressão.');
    return;
  }
  if (!popup) {
    window.alert('O navegador bloqueou a janela de impressão. Libere pop-ups para este site.');
    return;
  }

  const estabelecimento = sale.estabelecimento || {};
  const logoSrc = company.logoSrc || '';
  const logoHtml = logoSrc
    ? `<div class="center" style="margin-bottom:6px"><img src="${logoSrc}" alt="" style="max-height:48px;max-width:160px;object-fit:contain" /></div>`
    : '';
  const itemRows = (sale.itens || [])
    .map(
      (item) => `
        <tr>
          <td>
            <strong>${html(item.descricao)}</strong>
            <small>${html(item.codigo)} · ${html(item.quantidade)} ${html(item.unidade || 'UN')}
              × ${html(currency(item.valorUnitario))}</small>
          </td>
          <td class="number">${html(currency(item.valorTotal))}</td>
        </tr>`
    )
    .join('');
  const paymentRows = (sale.pagamentos || [])
    .map(
      (payment) => `
        <tr>
          <td>${html(payment.descricao)}${
            payment.bandeira ? ` · ${html(payment.bandeira)}` : ''
          }</td>
          <td class="number">${html(currency(payment.valor))}</td>
        </tr>`
    )
    .join('');

  popup.document.write(`<!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Venda #${html(sale.id)}</title>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { box-sizing: border-box; }
          body { width: 70mm; margin: 0 auto; color: #000; font: 12px Arial, sans-serif; }
          h1 { margin: 0; font-size: 17px; text-align: center; }
          .center { text-align: center; }
          .muted { font-size: 10px; }
          .separator { border-top: 1px dashed #000; margin: 8px 0; }
          table { width: 100%; border-collapse: collapse; }
          td { padding: 3px 0; vertical-align: top; }
          td.number { text-align: right; white-space: nowrap; padding-left: 5px; }
          small { display: block; font-size: 10px; }
          .total { font-size: 16px; font-weight: bold; }
          .cancelada { border: 2px solid #000; padding: 5px; margin: 8px 0; font-weight: bold; text-align: center; }
          .footer { margin-top: 14px; text-align: center; font-weight: bold; }
          @media screen { body { padding: 16px 0; } }
        </style>
      </head>
      <body>
        ${logoHtml}
        <h1>${html(estabelecimento.nome || company.nomeFantasia || 'ESTABELECIMENTO')}</h1>
        ${estabelecimento.razaoSocial && estabelecimento.razaoSocial !== estabelecimento.nome
          ? `<div class="center">${html(estabelecimento.razaoSocial)}</div>`
          : ''}
        <div class="center muted">
          ${estabelecimento.cnpj ? `CNPJ: ${html(estabelecimento.cnpj)}<br>` : ''}
          ${estabelecimento.inscricaoEstadual
            ? `IE: ${html(estabelecimento.inscricaoEstadual)}<br>`
            : ''}
          ${html(estabelecimento.endereco || '').replaceAll('\n', '<br>')}
          ${estabelecimento.telefone ? `<br>Telefone: ${html(estabelecimento.telefone)}` : ''}
        </div>
        <div class="separator"></div>
        <div class="center"><strong>${
          fiscal
            ? 'COMPROVANTE FISCAL — NFC-e'
            : 'COMPROVANTE SEM VALOR FISCAL'
        }</strong></div>
        ${fiscal && sale.numeroNfce
          ? `<div class="center muted">NFC-e nº ${html(sale.numeroNfce)} série ${html(sale.serieNfce || 1)}</div>`
          : ''}
        ${fiscal && sale.chaveNfce
          ? `<div class="center muted">Chave ${html(sale.chaveNfce)}</div>`
          : ''}
        <div>Venda: #${html(sale.id)}</div>
        <div>Data: ${html(new Date(sale.data).toLocaleString('pt-BR'))}</div>
        <div>PDV: ${html(sale.pdv)} · Operador: ${html(sale.usuario)}</div>
        <div>Cliente: ${html(sale.cliente)}</div>
        ${sale.cancelada ? '<div class="cancelada">VENDA CANCELADA</div>' : ''}
        <div class="separator"></div>
        <table>${itemRows}</table>
        <div class="separator"></div>
        <table>
          <tr><td>Subtotal</td><td class="number">${html(currency(sale.subtotal))}</td></tr>
          ${Number(sale.desconto) !== 0
            ? `<tr><td>Desconto</td><td class="number">-${html(currency(sale.desconto))}</td></tr>`
            : ''}
          ${Number(sale.acrescimo) !== 0
            ? `<tr><td>Acréscimo</td><td class="number">${html(currency(sale.acrescimo))}</td></tr>`
            : ''}
          <tr class="total"><td>TOTAL</td><td class="number">${html(currency(sale.valorTotal))}</td></tr>
        </table>
        <div class="separator"></div>
        <strong>Pagamentos</strong>
        <table>${paymentRows}</table>
        ${Number(sale.troco) !== 0
          ? `<div>Troco: <strong>${html(currency(sale.troco))}</strong></div>`
          : ''}
        ${sale.observacao ? `<div class="separator"></div><div>Obs.: ${html(sale.observacao)}</div>` : ''}
        <div class="footer">OBRIGADO PELA PREFERÊNCIA!<br>VOLTE SEMPRE!</div>
      </body>
    </html>`);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 250);
};

const reportPdfContent = (tab, data) => {
  if (tab === 0) {
    return {
      title: 'Relatório de vendas',
      summary: [
        ['Faturamento', currency(data?.faturamento)],
        ['Vendas', data?.quantidade || 0],
        ['Ticket médio', currency(data?.ticketMedio)],
        ['Descontos', currency(data?.descontos)],
        ['Canceladas', data?.canceladas || 0],
      ],
      headers: ['Venda/Data', 'PDV/Operador', 'Cliente', 'Desconto', 'Valor', 'Status'],
      rows: (data?.items || []).map((x) => [
        `#${x.id}\n${new Date(x.data).toLocaleString('pt-BR')}`,
        `${x.pdv || ''}\n${x.usuario || ''}`,
        x.cliente,
        currency(x.desconto),
        currency(x.valor),
        x.cancelada ? 'Cancelada' : 'Válida',
      ]),
    };
  }
  if (tab === 1) {
    const rows = data || [];
    return {
      title: 'Relatório por forma de pagamento',
      summary: [
        ['Total', currency(rows.reduce((sum, x) => sum + Number(x.valor || 0), 0))],
        ['Transações', rows.reduce((sum, x) => sum + Number(x.transacoes || 0), 0)],
      ],
      headers: ['Forma', 'Transações', 'Participação', 'Valor'],
      rows: rows.map((x) => [
        x.descricao,
        x.transacoes,
        `${Number(x.percentual || 0).toFixed(2)}%`,
        currency(x.valor),
      ]),
    };
  }
  if (tab === 2) {
    const rows = data || [];
    return {
      title: 'Relatório de produtos vendidos',
      summary: [
        ['Produtos', rows.length],
        ['Quantidade', rows.reduce((sum, x) => sum + Number(x.quantidade || 0), 0).toLocaleString('pt-BR')],
        ['Valor', currency(rows.reduce((sum, x) => sum + Number(x.valor || 0), 0))],
        ['Descontos', currency(rows.reduce((sum, x) => sum + Number(x.desconto || 0), 0))],
      ],
      headers: ['Código', 'Produto', 'Quantidade', 'Desconto', 'Valor'],
      rows: rows.map((x) => [
        x.codigo,
        x.descricao,
        Number(x.quantidade || 0).toLocaleString('pt-BR'),
        currency(x.desconto),
        currency(x.valor),
      ]),
    };
  }
  if (tab === 3) {
    return {
      title: 'Relatório de convênios',
      summary: [
        ['Lançamentos', currency(data?.lancamentos)],
        ['Receb. manuais', currency(data?.recebimentosManuais)],
        ['Cashback', currency(data?.cashback)],
        ['Créditos (total)', currency(data?.recebimentos)],
        ['Saldo', currency(data?.saldo)],
      ],
      headers: [
        'Cliente',
        'Limite',
        'Lançamentos',
        'Receb. manuais',
        'Cashback',
        'Créditos',
        'Saldo',
        'Disponível (Saldo + Limite)',
        'Situação',
      ],
      rows: (data?.clientes || []).map((x) => [
        x.cliente,
        currency(x.limite),
        currency(x.lancamentos),
        currency(x.recebimentosManuais),
        currency(x.cashback),
        currency(x.recebimentos),
        currency(x.saldo),
        currency(x.disponivel),
        x.bloqueado ? 'Bloqueado' : 'Ativo',
      ]),
    };
  }
  if (tab === 4) {
    return {
      title: 'Relatório de cashback',
      summary: [
        ['Total cashback', currency(data?.totalCashback)],
        ['Lançamentos', data?.quantidade || 0],
        ['Clientes', (data?.porCliente || []).length],
      ],
      headers: ['Data', 'Cliente', 'Venda', 'Observação', 'Valor'],
      rows: (data?.itens || []).map((x) => [
        new Date(x.data).toLocaleString('pt-BR'),
        x.cliente,
        x.vendaId ? `#${x.vendaId}` : '—',
        x.observacao || '',
        currency(x.valor),
      ]),
    };
  }
  if (tab === 5) {
    return {
      title: 'Vendas por vendedor',
      summary: [
        ['Faturamento', currency(data?.faturamento)],
        ['Comissão', currency(data?.comissao)],
        ['Vendas', data?.quantidadeVendas || 0],
      ],
      headers: ['Vendedor', 'Vendas', 'Faturamento', 'Comissão', 'Ticket médio'],
      rows: (data?.vendedores || []).map((x) => [
        x.vendedor,
        x.quantidadeVendas,
        currency(x.faturamento),
        currency(x.comissao),
        currency(x.ticketMedio),
      ]),
    };
  }
  if (tab === 6) {
    return {
      title: 'Comissão por vendedor',
      summary: [
        ['Total comissão', currency(data?.totalComissao)],
        ['Vendas', data?.quantidadeVendas || 0],
      ],
      headers: ['Vendedor', 'Vendas', 'Faturamento', 'Comissão', '% médio'],
      rows: (data?.itens || []).map((x) => [
        x.vendedor,
        x.quantidadeVendas,
        currency(x.faturamento),
        currency(x.comissao),
        `${Number(x.percentualMedio || 0).toFixed(2)}%`,
      ]),
    };
  }
  return {
    title: 'Produtos por vendedor',
    summary: [
      ['Quantidade', Number(data?.quantidade || 0).toLocaleString('pt-BR')],
      ['Valor', currency(data?.valor)],
      ['Comissão', currency(data?.comissao)],
      ['Linhas', (data?.itens || []).length],
    ],
    headers: ['Vendedor', 'Código', 'Produto', 'Quantidade', 'Desconto', 'Valor', 'Comissão'],
    rows: (data?.itens || []).map((x) => [
      x.vendedor,
      x.codigo,
      x.descricao,
      Number(x.quantidade || 0).toLocaleString('pt-BR'),
      currency(x.desconto),
      currency(x.valor),
      currency(x.comissao),
    ]),
  };
};

const printReportPdf = (popup, tab, data, period, company = {}) => {
  const report = reportPdfContent(tab, data);
  const summary = report.summary
    .map(([label, value]) => `<div><span>${html(label)}</span><strong>${html(value)}</strong></div>`)
    .join('');
  const head = report.headers.map((value) => `<th>${html(value)}</th>`).join('');
  const rows = report.rows
    .map(
      (row) =>
        `<tr>${row
          .map((value, index) => `<td class="${index > 2 ? 'right' : ''}">${html(value).replaceAll('\n', '<br>')}</td>`)
          .join('')}</tr>`
    )
    .join('');

  popup.document.open();
  popup.document.write(`<!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>${html(report.title)} - ${html(period)}</title>
        <style>
          @page { size: A4 landscape; margin: 12mm; }
          * { box-sizing: border-box; }
          body { margin: 0; color: #172033; font: 10px Arial, sans-serif; }
          header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #0495f0; padding-bottom: 8px; gap: 16px; }
          h1 { margin: 2px 0 0; font-size: 20px; }
          ${REPORT_BRAND_CSS}
          .period { color: #5f6878; text-align: right; }
          .summary { display: flex; gap: 8px; margin: 12px 0; }
          .summary div { min-width: 130px; border: 1px solid #d8dde7; border-radius: 5px; padding: 7px 9px; }
          .summary span, .summary strong { display: block; }
          .summary span { color: #687386; font-size: 9px; }
          .summary strong { margin-top: 2px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          th { background: #eaf6fe; color: #172033; text-align: left; padding: 6px; border-bottom: 1px solid #9fcfec; }
          td { padding: 5px 6px; border-bottom: 1px solid #e2e6ed; vertical-align: top; }
          td.right { text-align: right; }
          footer { position: fixed; bottom: -7mm; right: 0; color: #737c8c; font-size: 8px; }
          @media screen { body { padding: 18px; } }
        </style>
      </head>
      <body>
        <header>
          ${reportBrandHtml(company, { title: report.title })}
          <div class="period">${tab === 3 ? 'Posição atual da conta' : `Período: ${html(period)}`}<br>Emitido em ${html(new Date().toLocaleString('pt-BR'))}</div>
        </header>
        <section class="summary">${summary}</section>
        <table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>
        <footer>ZenGestão · ${html(report.rows.length)} registros</footer>
      </body>
    </html>`);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 300);
};

function Metric({ label, value, color = 'text.primary' }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={color} fontWeight={900}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const [searchParams] = useSearchParams();
  const fromUrl = useMemo(() => initialFilters(searchParams), [searchParams]);
  const [filters, setFilters] = useState(fromUrl);
  const [applied, setApplied] = useState(fromUrl);
  const filtrosApi = useMemo(
    () => ({ ...applied, estabelecimentoId }),
    [applied, estabelecimentoId],
  );
  const [tab, setTab] = useState(() => tabFromParams(searchParams));
  const [sinalConvenio, setSinalConvenio] = useState('todos');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [saleDetail, setSaleDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waVendaId, setWaVendaId] = useState(null);
  const [waTelefone, setWaTelefone] = useState('');
  const [waSaving, setWaSaving] = useState(false);
  const [waOk, setWaOk] = useState('');
  const [nfceReemitSaving, setNfceReemitSaving] = useState(false);
  const [nfceAvancarOpen, setNfceAvancarOpen] = useState(false);
  const podeCancelar = useMemo(() => canCancelSale(), []);
  const podeAlterarPagamento = useMemo(() => canAlterarFormaPagamento(), []);

  // Dashboard manda de/ate/tab na URL: reaplica quando a query muda.
  useEffect(() => {
    const next = initialFilters(searchParams);
    setFilters(next);
    setApplied(next);
    setTab(tabFromParams(searchParams));
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersResp, pdvsResp, catsResp] = await Promise.all([
          getAllUsers(),
          getAllPdvs(),
          getAllCategories({ indice: 1, qtde: 200 }),
        ]);
        const list = Array.isArray(usersResp.data) ? usersResp.data : [];
        const pdvList = Array.isArray(pdvsResp.data) ? pdvsResp.data : [];
        const catList = Array.isArray(catsResp.data?.items)
          ? catsResp.data.items
          : Array.isArray(catsResp.data)
            ? catsResp.data
            : [];
        if (!cancelled) {
          setVendedores(
            list
              .filter((u) => u?.ativo !== false)
              .sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR')),
          );
          setPdvs(
            pdvList
              .slice()
              .sort((a, b) =>
                String(a.descricao || '').localeCompare(String(b.descricao || ''), 'pt-BR'),
              ),
          );
          setCategorias(
            catList
              .slice()
              .sort((a, b) =>
                String(a.descricao || '').localeCompare(String(b.descricao || ''), 'pt-BR'),
              ),
          );
        }
      } catch {
        if (!cancelled) {
          setVendedores([]);
          setPdvs([]);
          setCategorias([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openWhatsApp = (id, telefone) => {
    setError('');
    setWaOk('');
    setWaVendaId(id);
    setWaTelefone(formatPhone(telefone));
    setWaOpen(true);
  };

  const handleEnviarWhatsApp = async () => {
    const tel = digitsPhone(waTelefone);
    if (tel.length < 10) {
      setError('Informe o WhatsApp com DDD.');
      return;
    }
    setWaSaving(true);
    setError('');
    try {
      await enviarWhatsAppVenda(waVendaId, tel);
      setWaOpen(false);
      setWaOk('Cupom enviado pelo WhatsApp.');
      await load();
      if (saleDetail?.id === waVendaId) {
        const response = await getSaleReportDetail(waVendaId);
        setSaleDetail(response.data);
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível enviar o WhatsApp.'));
    } finally {
      setWaSaving(false);
    }
  };

  const executarReemitirNfce = async (avancarNumeracao) => {
    if (!saleDetail?.id) return;
    setNfceAvancarOpen(false);
    setNfceReemitSaving(true);
    setError('');
    setWaOk('');
    try {
      await reemitirNfceVenda(saleDetail.id, { avancarNumeracao });
      const response = await getSaleReportDetail(saleDetail.id);
      setSaleDetail(response.data);
      await load();
      const st = String(response.data?.statusFiscal || '').toLowerCase();
      if (st === 'autorizada') {
        setWaOk('NFC-e autorizada.');
      } else if (st === 'fiscal_rejeitada') {
        setError(response.data?.mensagemFiscal || 'NFC-e rejeitada novamente.');
      }
    } catch (e) {
      const errMsg = getApiErrorMessage(e, 'Não foi possível reenviar a NFC-e.');
      setError(errMsg);
    } finally {
      setNfceReemitSaving(false);
    }
  };

  const handleReemitirNfce = async () => {
    if (!saleDetail?.id) return;
    const msg = String(saleDetail.mensagemFiscal || '');
    const duplicidade =
      /\b(204|539)\b/.test(msg) || /duplicidade/i.test(msg);
    if (duplicidade) {
      setNfceAvancarOpen(true);
      return;
    }
    await executarReemitirNfce(false);
  };

  const openSaleDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSaleDetail(null);
    try {
      const response = await getSaleReportDetail(id);
      setSaleDetail(response.data);
    } catch (e) {
      setDetailOpen(false);
      setError(e?.response?.data?.error || 'Não foi possível carregar os detalhes da venda.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancelSale = async () => {
    if (!saleDetail?.id) return;
    const motivo = cancelMotivo.trim();
    const temNfce = saleDetail.statusFiscal === 'autorizada';
    if (temNfce && motivo.length < 15) {
      setError('Justificativa da NFC-e deve ter ao menos 15 caracteres.');
      return;
    }
    if (!motivo) {
      setError('Informe o motivo do cancelamento.');
      return;
    }
    setCancelSaving(true);
    setError('');
    try {
      if (temNfce) await cancelarNfceVenda(saleDetail.id, motivo);
      else await cancelSale(saleDetail.id, motivo);
      setCancelOpen(false);
      setCancelMotivo('');
      const response = await getSaleReportDetail(saleDetail.id);
      setSaleDetail(response.data);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível cancelar a venda.'));
    } finally {
      setCancelSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      if (tab === 0) {
        response = await getSalesReport({ ...filtrosApi, indice: page, qtde: 50 });
      } else if (tab === 1) {
        response = await getPaymentMethodsReport(filtrosApi);
      } else if (tab === 2) {
        response = await getProductsReport({ ...filtrosApi, limite: 200 });
      } else if (tab === 3) {
        response = await getConvenioReport();
      } else if (tab === 4) {
        response = await getCashbackReport(filtrosApi);
      } else if (tab === 5) {
        response = await getSalesBySellerReport(filtrosApi);
      } else if (tab === 6) {
        response = await getCommissionBySellerReport(filtrosApi);
      } else {
        response = await getProductsBySellerReport({ ...filtrosApi, limite: 500 });
      }
      setData(response.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível gerar o relatório.');
    } finally {
      setLoading(false);
    }
  }, [filtrosApi, page, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCurrent = () => {
    const period = `${applied.de}_${applied.ate}`;
    if (tab === 0) {
      downloadCsv(
        `vendas_${period}.csv`,
        ['Venda', 'Data', 'PDV', 'Operador', 'Cliente', 'Valor', 'Desconto', 'Status'],
        (data?.items || []).map((x) => [
          x.id,
          new Date(x.data).toLocaleString('pt-BR'),
          x.pdv,
          x.usuario,
          x.cliente,
          Number(x.valor).toFixed(2),
          Number(x.desconto).toFixed(2),
          x.cancelada ? 'Cancelada' : 'Válida',
        ])
      );
    } else if (tab === 1) {
      downloadCsv(
        `formas_pagamento_${period}.csv`,
        ['Forma', 'Transações', 'Valor', 'Percentual'],
        (data || []).map((x) => [
          x.descricao,
          x.transacoes,
          Number(x.valor).toFixed(2),
          Number(x.percentual).toFixed(2),
        ])
      );
    } else if (tab === 2) {
      downloadCsv(
        `produtos_${period}.csv`,
        ['Código', 'Produto', 'Quantidade', 'Valor', 'Desconto'],
        (data || []).map((x) => [
          x.codigo,
          x.descricao,
          x.quantidade,
          Number(x.valor).toFixed(2),
          Number(x.desconto).toFixed(2),
        ])
      );
    } else if (tab === 3) {
      downloadCsv(
        'convenios.csv',
        [
          'Cliente',
          'Limite',
          'Lançamentos',
          'Receb. manuais',
          'Cashback',
          'Créditos',
          'Saldo',
          'Disponível (Saldo + Limite)',
          'Bloqueado',
        ],
        convenioFiltrado(data, sinalConvenio).clientes.map((x) => [
          x.cliente,
          Number(x.limite).toFixed(2),
          Number(x.lancamentos).toFixed(2),
          Number(x.recebimentosManuais || 0).toFixed(2),
          Number(x.cashback || 0).toFixed(2),
          Number(x.recebimentos).toFixed(2),
          Number(x.saldo).toFixed(2),
          Number(x.disponivel).toFixed(2),
          x.bloqueado ? 'Sim' : 'Não',
        ])
      );
    } else if (tab === 4) {
      downloadCsv(
        `cashback_${period}.csv`,
        ['Data', 'Cliente', 'Venda', 'Observação', 'Valor'],
        (data?.itens || []).map((x) => [
          new Date(x.data).toLocaleString('pt-BR'),
          x.cliente,
          x.vendaId || '',
          x.observacao || '',
          Number(x.valor).toFixed(2),
        ])
      );
    } else if (tab === 5) {
      downloadCsv(
        `vendas_por_vendedor_${period}.csv`,
        ['Vendedor', 'Vendas', 'Faturamento', 'Comissão', 'Ticket médio'],
        (data?.vendedores || []).map((x) => [
          x.vendedor,
          x.quantidadeVendas,
          Number(x.faturamento).toFixed(2),
          Number(x.comissao).toFixed(2),
          Number(x.ticketMedio).toFixed(2),
        ])
      );
    } else if (tab === 6) {
      downloadCsv(
        `comissao_por_vendedor_${period}.csv`,
        ['Vendedor', 'Vendas', 'Faturamento', 'Comissão', '% médio'],
        (data?.itens || []).map((x) => [
          x.vendedor,
          x.quantidadeVendas,
          Number(x.faturamento).toFixed(2),
          Number(x.comissao).toFixed(2),
          Number(x.percentualMedio).toFixed(2),
        ])
      );
    } else {
      downloadCsv(
        `produtos_por_vendedor_${period}.csv`,
        ['Vendedor', 'Código', 'Produto', 'Quantidade', 'Desconto', 'Valor', 'Comissão'],
        (data?.itens || []).map((x) => [
          x.vendedor,
          x.codigo,
          x.descricao,
          x.quantidade,
          Number(x.desconto).toFixed(2),
          Number(x.valor).toFixed(2),
          Number(x.comissao).toFixed(2),
        ])
      );
    }
  };

  const exportPdf = async () => {
    // A janela precisa ser aberta no clique; abri-la depois dos awaits faz o
    // navegador tratá-la como pop-up e bloqueá-la.
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) {
      setError('O navegador bloqueou a geração do PDF. Libere pop-ups para este site.');
      return;
    }
    popup.document.write(
      '<!doctype html><title>Gerando relatório</title><p style="font:16px Arial;padding:24px">Preparando relatório completo…</p>'
    );

    try {
      const company = await loadReportCompany();
      let pdfData;
      if (tab === 0) {
        const first = await getSalesReport({ ...filtrosApi, indice: 1, qtde: 200 });
        pdfData = first.data;
        const totalPages = Number(first.data?.totalPages || 1);
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              getSalesReport({ ...filtrosApi, indice: index + 2, qtde: 200 })
            )
          );
          pdfData = {
            ...pdfData,
            items: [
              ...(pdfData?.items || []),
              ...remaining.flatMap((response) => response.data?.items || []),
            ],
          };
        }
      } else if (tab === 1) {
        pdfData = (await getPaymentMethodsReport(filtrosApi)).data;
      } else if (tab === 2) {
        pdfData = (await getProductsReport({ ...filtrosApi, limite: 500 })).data;
      } else if (tab === 3) {
        pdfData = convenioFiltrado((await getConvenioReport()).data, sinalConvenio);
      } else if (tab === 4) {
        pdfData = (await getCashbackReport(filtrosApi)).data;
      } else if (tab === 5) {
        pdfData = (await getSalesBySellerReport(filtrosApi)).data;
      } else if (tab === 6) {
        pdfData = (await getCommissionBySellerReport(filtrosApi)).data;
      } else {
        pdfData = (await getProductsBySellerReport({ ...filtrosApi, limite: 2000 })).data;
      }

      printReportPdf(
        popup,
        tab,
        pdfData,
        `${applied.de} a ${applied.ate}`,
        company
      );
    } catch (e) {
      popup.close();
      setError(e?.response?.data?.error || 'Não foi possível gerar o PDF do relatório.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Relatórios"
        subtitle="Dados consolidados das operações sincronizadas."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => navigate('/reports/cancellations')}
            >
              Cancelamentos
            </Button>
            <Button
              variant="outlined"
              startIcon={<InsightsIcon />}
              onClick={() => navigate('/reports/abc')}
            >
              Curva ABC
            </Button>
            <Button
              variant="outlined"
              startIcon={<PointOfSaleIcon />}
              onClick={() => navigate('/cash-sessions')}
            >
              Sessões de caixa
            </Button>
          </>
        }
      />

      <Paper sx={{ mt: 0, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value);
            setPage(1);
            setData(null);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Vendas" />
          <Tab label="Formas de pagamento" />
          <Tab label="Produtos" />
          <Tab label="Convênio" />
          <Tab label="Cashback" />
          <Tab label="Vendas por vendedor" />
          <Tab label="Comissão por vendedor" />
          <Tab label="Produtos por vendedor" />
        </Tabs>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ p: 2 }} useFlexGap flexWrap="wrap">
          {tab !== 3 && (
            <TextField
              label="De"
              type="date"
              size="small"
              value={filters.de}
              onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {tab === 3 && (
            <TextField
              select
              label="Saldo"
              size="small"
              value={sinalConvenio}
              onChange={(e) => setSinalConvenio(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="negativos">Negativos</MenuItem>
              <MenuItem value="positivos">Positivos</MenuItem>
            </TextField>
          )}
          {tab !== 3 && (
            <TextField
              label="Até"
              type="date"
              size="small"
              value={filters.ate}
              onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {tab === 0 && (
            <TextField
              select
              label="Status"
              size="small"
              value={filters.status || ''}
              onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="validas">Válidas</MenuItem>
              <MenuItem value="canceladas">Canceladas</MenuItem>
            </TextField>
          )}
          {tab === 0 && (
            <TextField
              select
              label="PDV"
              size="small"
              value={filters.pdvId || ''}
              onChange={(e) => setFilters((p) => ({ ...p, pdvId: e.target.value }))}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {pdvs.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.descricao || `PDV ${p.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          {tab === 0 && (
            <TextField
              select
              label="Operador"
              size="small"
              value={filters.usuarioId || ''}
              onChange={(e) => setFilters((p) => ({ ...p, usuarioId: e.target.value }))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {vendedores.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.nome || `Usuário ${u.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          {(tab === 0 || tab === 5 || tab === 6 || tab === 7) && (
            <TextField
              select
              label="Vendedor"
              size="small"
              value={filters.vendedorId || ''}
              onChange={(e) => setFilters((p) => ({ ...p, vendedorId: e.target.value }))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {vendedores.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.nome || `Usuário ${u.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          {(tab === 2 || tab === 7) && (
            <TextField
              label="Buscar produto"
              size="small"
              placeholder="Código ou descrição"
              value={filters.busca || ''}
              onChange={(e) => setFilters((p) => ({ ...p, busca: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  setApplied({ ...filters, busca: e.target.value });
                }
              }}
              sx={{ minWidth: 220 }}
            />
          )}
          {(tab === 2 || tab === 7) && (
            <Autocomplete
              multiple
              size="small"
              options={categorias}
              disableCloseOnSelect
              limitTags={2}
              getOptionLabel={(opt) => opt?.descricao || `Categoria ${opt?.id}`}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              value={categorias.filter((c) =>
                (filters.categoriaIds || []).map(Number).includes(Number(c.id)),
              )}
              onChange={(_, selected) =>
                setFilters((p) => ({
                  ...p,
                  categoriaIds: selected.map((c) => String(c.id)),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={option.descricao || option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Categorias" placeholder="Todas" />
              )}
              sx={{ minWidth: 260, flex: 1 }}
            />
          )}
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => {
              setPage(1);
              // Cópia nova a cada clique: sem trocar a referência o React
              // descarta o setState e o relatório não recarrega.
              setApplied({ ...filters });
            }}
          >
            Gerar
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!data}
            onClick={exportCurrent}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            disabled={!data || loading}
            onClick={exportPdf}
          >
            PDF
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {waOk && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setWaOk('')}>{waOk}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {tab === 0 && (
        <SalesReport data={data} onDetail={openSaleDetail} onWhatsApp={openWhatsApp} />
      )}
      {tab === 1 && <PaymentReport data={data} />}
      {tab === 2 && <ProductReport data={data} />}
      {tab === 3 && <ConvenioReport data={convenioFiltrado(data, sinalConvenio)} />}
      {tab === 4 && <CashbackReport data={data} />}
      {tab === 5 && <VendasPorVendedorReport data={data} />}
      {tab === 6 && <ComissaoPorVendedorReport data={data} />}
      {tab === 7 && <ProdutosPorVendedorReport data={data} />}

      {tab === 0 && data?.totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <SaleDetailDialog
        open={detailOpen}
        loading={detailLoading}
        sale={saleDetail}
        canCancel={podeCancelar}
        canAlterarPagamento={podeAlterarPagamento}
        onPagamentoAlterado={async () => {
          if (!saleDetail?.id) return;
          const response = await getSaleReportDetail(saleDetail.id);
          setSaleDetail(response.data);
          await load();
        }}
        onClose={() => setDetailOpen(false)}
        onPrint={async () => {
          try {
            const company = await loadReportCompany();
            await printSale(saleDetail, company);
          } catch {
            await printSale(saleDetail);
          }
        }}
        onCancel={() => {
          setCancelMotivo('');
          setCancelOpen(true);
        }}
        onWhatsApp={() => openWhatsApp(saleDetail?.id, saleDetail?.whatsAppTelefone)}
        onReemitirNfce={handleReemitirNfce}
        nfceReemitSaving={nfceReemitSaving}
      />

      <Dialog
        open={waOpen}
        onClose={() => !waSaving && setWaOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enviar cupom no WhatsApp</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Número"
            placeholder="(11) 99999-9999"
            value={waTelefone}
            onChange={(e) => setWaTelefone(formatPhone(e.target.value))}
            helperText="Se a venda já foi enviada, o número vem preenchido."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaOpen(false)} disabled={waSaving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={waSaving || digitsPhone(waTelefone).length < 10}
            onClick={handleEnviarWhatsApp}
            startIcon={<WhatsAppIcon />}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}
          >
            {waSaving ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={nfceAvancarOpen}
        onClose={() => !nfceReemitSaving && setNfceAvancarOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicidade de numeração</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A SEFAZ rejeitou por duplicidade (204/539). A numeração
            {' '}
            <strong>{saleDetail?.numeroNfce || '?'}</strong>
            {' '}
            já foi utilizada anteriormente.
          </Alert>
          Deseja avançar para o próximo número da sequência e reenviar a NFC-e?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNfceAvancarOpen(false)} disabled={nfceReemitSaving}>
            Voltar
          </Button>
          <Button
            variant="contained"
            disabled={nfceReemitSaving}
            onClick={() => executarReemitirNfce(true)}
          >
            {nfceReemitSaving ? 'Reenviando...' : 'Avançar numeração e reenviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => !cancelSaving && setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar venda #{saleDetail?.id}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {saleDetail?.statusFiscal === 'autorizada'
              ? 'Cancela a NFC-e na SEFAZ e estorna estoque/convênio. Justificativa mínima de 15 caracteres.'
              : 'O cancelamento estorna o estoque e remove lançamentos de convênio.'}
            {' '}Pagamentos em cartão (TEF) precisam ser estornados na maquininha.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label={
              saleDetail?.statusFiscal === 'autorizada'
                ? 'Justificativa (mín. 15 caracteres)'
                : 'Motivo do cancelamento'
            }
            value={cancelMotivo}
            onChange={(e) => setCancelMotivo(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={cancelSaving}>
            Voltar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              cancelSaving ||
              (saleDetail?.statusFiscal === 'autorizada'
                ? cancelMotivo.trim().length < 15
                : !cancelMotivo.trim())
            }
            onClick={handleCancelSale}
          >
            {cancelSaving ? 'Cancelando...' : 'Confirmar cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SaleDetailDialog({
  open,
  loading,
  sale,
  onClose,
  onPrint,
  onCancel,
  onWhatsApp,
  onReemitirNfce,
  nfceReemitSaving,
  canCancel,
  canAlterarPagamento,
  onPagamentoAlterado,
}) {
  const [alterOpen, setAlterOpen] = useState(false);
  const nfceAutorizada = sale?.statusFiscal === 'autorizada';
  const nfceRejeitada = sale?.statusFiscal === 'fiscal_rejeitada';
  const nfcePendente =
    sale?.statusFiscal === 'fiscal_pendente' || sale?.statusFiscal === 'processando';
  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {sale ? `Venda #${sale.id}` : 'Detalhes da venda'}
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {!loading && sale && (
          <Stack spacing={2}>
            {sale.cancelada && (
              <Alert severity="error">
                Esta venda foi cancelada
                {sale.dataCancelamento
                  ? ` em ${new Date(sale.dataCancelamento).toLocaleString('pt-BR')}`
                  : ''}
                {sale.motivoCancelamento ? `. Motivo: ${sale.motivoCancelamento}` : '.'}
              </Alert>
            )}
            {sale.statusFiscal && (
              <Alert
                severity={
                  nfceAutorizada
                    ? 'success'
                    : sale.statusFiscal === 'cancelada' || nfceRejeitada
                      ? 'error'
                      : 'warning'
                }
              >
                NFC-e {sale.statusFiscal}
                {sale.numeroNfce
                  ? ` · ${sale.numeroNfce}/${sale.serieNfce || 1}`
                  : ''}
                {sale.chaveNfce ? ` · chave ${sale.chaveNfce}` : ''}
                {sale.mensagemFiscal ? (
                  <Box component="div" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {sale.mensagemFiscal}
                  </Box>
                ) : null}
              </Alert>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Data</Typography>
                <Typography>{new Date(sale.data).toLocaleString('pt-BR')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography>{sale.cliente}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">PDV</Typography>
                <Typography>{sale.pdv}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Operador</Typography>
                <Typography>{sale.usuario}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Vendedor</Typography>
                <Typography>
                  {sale.vendedor || '—'}
                  {Number(sale.valorComissao) > 0
                    ? ` · comissão ${currency(sale.valorComissao)}`
                    : ''}
                </Typography>
              </Grid>
              {sale.idExterno && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Identificador externo
                  </Typography>
                  <Typography sx={{ wordBreak: 'break-all' }}>{sale.idExterno}</Typography>
                </Grid>
              )}
            </Grid>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Itens
              </Typography>
              <ResponsiveTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código/Produto</TableCell>
                      <TableCell align="right">Qtd.</TableCell>
                      <TableCell align="right">Unitário</TableCell>
                      <TableCell align="right">Desconto</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(sale.itens || []).map((item, index) => (
                      <TableRow key={`${item.produtoId}-${index}`}>
                        <TableCell data-label="Código/produto">
                          {item.codigo}<br />
                          <small>{item.descricao}</small>
                        </TableCell>
                        <TableCell align="right" data-label="Qtd.">
                          {Number(item.quantidade).toLocaleString('pt-BR')} {item.unidade}
                        </TableCell>
                        <TableCell align="right" data-label="Unitário">{currency(item.valorUnitario)}</TableCell>
                        <TableCell align="right" data-label="Desconto">{currency(item.desconto)}</TableCell>
                        <TableCell align="right" data-label="Total"><strong>{currency(item.valorTotal)}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTable>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Pagamentos
                </Typography>
                <ResponsiveTable>
                  <Table size="small">
                    <TableBody>
                      {(sale.pagamentos || []).map((payment, index) => (
                        <TableRow key={`${payment.formaPagamentoId}-${index}`}>
                          <TableCell data-label="Forma">
                            {payment.descricao}
                            {(payment.bandeira || payment.rede) && (
                              <small>{[payment.bandeira, payment.rede].filter(Boolean).join(' · ')}</small>
                            )}
                          </TableCell>
                          <TableCell align="right" data-label="Valor"><strong>{currency(payment.valor)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTable>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>Subtotal</Typography>
                      <Typography>{currency(sale.subtotal)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>Desconto</Typography>
                      <Typography color="error.main">-{currency(sale.desconto)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>Acréscimo</Typography>
                      <Typography>{currency(sale.acrescimo)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={900}>Total</Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currency(sale.valorTotal)}
                      </Typography>
                    </Stack>
                    {Number(sale.troco) !== 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography>Troco</Typography>
                        <Typography>{currency(sale.troco)}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {sale.observacao && (
              <Alert severity="info">Observação: {sale.observacao}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {canCancel && sale && !sale.cancelada && (
          <Button
            color="error"
            variant="outlined"
            startIcon={<CancelOutlinedIcon />}
            disabled={loading}
            onClick={onCancel}
          >
            Cancelar venda
          </Button>
        )}
        {canAlterarPagamento && sale && !sale.cancelada && (
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => setAlterOpen(true)}
          >
            Alterar formas
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<WhatsAppIcon />}
          disabled={!sale || loading || sale.cancelada}
          onClick={onWhatsApp}
          sx={{ color: '#25D366', borderColor: '#25D366' }}
        >
          WhatsApp
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          disabled={!sale || loading || nfceRejeitada || nfcePendente}
          onClick={onPrint}
        >
          Imprimir cupom
        </Button>
        {(nfceRejeitada || nfcePendente) && (
          <Button
            variant="contained"
            color="warning"
            disabled={!sale || loading || nfceReemitSaving || sale.cancelada}
            onClick={onReemitirNfce}
          >
            {nfceReemitSaving ? 'Reenviando...' : nfceRejeitada && /\b(204|539)\b|duplicidade/i.test(String(sale?.mensagemFiscal || ''))
              ? 'Avançar nº e reenviar'
              : 'Reenviar NFC-e'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    <AlterarFormasDialog
      open={alterOpen}
      sale={sale}
      onClose={() => setAlterOpen(false)}
      onSaved={async () => {
        setAlterOpen(false);
        if (onPagamentoAlterado) await onPagamentoAlterado();
      }}
    />
    </>
  );
}

function AlterarFormasDialog({ open, sale, onClose, onSaved }) {
  const [formas, setFormas] = useState([]);
  const [linhas, setLinhas] = useState([{ formaPagamentoId: '', valor: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !sale) return;
    setError('');
    setLinhas(
      (sale.pagamentos || []).length
        ? sale.pagamentos.map((p) => ({
            formaPagamentoId: String(p.formaPagamentoId),
            valor: String(p.valor ?? ''),
          }))
        : [{ formaPagamentoId: '', valor: String(sale.valorTotal || '') }],
    );
    getAllPaymentMethods()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setFormas(
          list.filter(
            (f) =>
              !f.tef &&
              !f.Tef &&
              !f.convenio &&
              !f.Convenio &&
              !f.pixIntegrado &&
              !f.PixIntegrado,
          ),
        );
      })
      .catch(() => setFormas([]));
  }, [open, sale]);

  const totalLinhas = linhas.reduce(
    (s, l) => s + (Number(String(l.valor).replace(',', '.')) || 0),
    0,
  );

  const salvar = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = linhas.map((l) => ({
        formaPagamentoId: Number(l.formaPagamentoId),
        valor: Number(String(l.valor).replace(',', '.')),
        troco: 0,
      }));
      await alterarFormasPagamentoVenda(sale.id, payload);
      await onSaved?.();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível alterar as formas.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alterar formas de pagamento</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            Não é permitido TEF, Pix integrado ou Convênio (origem ou destino).
            Total da venda: {currency(sale?.valorTotal)} · Soma: {currency(totalLinhas)}
          </Alert>
          {linhas.map((linha, idx) => (
            <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                select
                label="Forma"
                size="small"
                fullWidth
                value={linha.formaPagamentoId}
                onChange={(e) => {
                  const next = [...linhas];
                  next[idx] = { ...next[idx], formaPagamentoId: e.target.value };
                  setLinhas(next);
                }}
              >
                {formas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.descricao}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Valor"
                size="small"
                value={linha.valor}
                onChange={(e) => {
                  const next = [...linhas];
                  next[idx] = { ...next[idx], valor: e.target.value };
                  setLinhas(next);
                }}
              />
              <Button
                color="inherit"
                disabled={linhas.length <= 1}
                onClick={() => setLinhas(linhas.filter((_, i) => i !== idx))}
              >
                Remover
              </Button>
            </Stack>
          ))}
          <Button
            onClick={() =>
              setLinhas([...linhas, { formaPagamentoId: '', valor: '' }])
            }
          >
            Adicionar forma
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" disabled={saving} onClick={salvar}>
          {saving ? 'Salvando...' : 'Gravar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VendasPorVendedorReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.vendedores || [],
    'faturamento',
    'desc',
  );
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={4}>
          <Metric label="Faturamento" value={currency(data?.faturamento)} />
        </Grid>
        <Grid item xs={6} md={4}>
          <Metric label="Comissão" value={currency(data?.comissao)} />
        </Grid>
        <Grid item xs={6} md={4}>
          <Metric label="Vendas" value={data?.quantidadeVendas || 0} />
        </Grid>
      </Grid>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="vendedor" label="Vendedor" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="quantidadeVendas" label="Vendas" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="faturamento" label="Faturamento" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="comissao" label="Comissão" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="ticketMedio" label="Ticket médio" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma venda com vendedor no período.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((x) => (
              <TableRow key={x.vendedorId}>
                <TableCell data-label="Vendedor">{x.vendedor}</TableCell>
                <TableCell align="right" data-label="Vendas">{x.quantidadeVendas}</TableCell>
                <TableCell align="right" data-label="Faturamento">
                  {currency(x.faturamento)}
                </TableCell>
                <TableCell align="right" data-label="Comissão">
                  <strong>{currency(x.comissao)}</strong>
                </TableCell>
                <TableCell align="right" data-label="Ticket">
                  {currency(x.ticketMedio)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}

function ComissaoPorVendedorReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.itens || [],
    'comissao',
    'desc',
  );
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={6}>
          <Metric label="Total comissão" value={currency(data?.totalComissao)} />
        </Grid>
        <Grid item xs={6} md={6}>
          <Metric label="Vendas" value={data?.quantidadeVendas || 0} />
        </Grid>
      </Grid>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="vendedor" label="Vendedor" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="quantidadeVendas" label="Vendas" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="faturamento" label="Faturamento" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="comissao" label="Comissão" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="percentualMedio" label="% médio" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma comissão no período.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((x) => (
              <TableRow key={x.vendedorId}>
                <TableCell data-label="Vendedor">{x.vendedor}</TableCell>
                <TableCell align="right" data-label="Vendas">{x.quantidadeVendas}</TableCell>
                <TableCell align="right" data-label="Faturamento">
                  {currency(x.faturamento)}
                </TableCell>
                <TableCell align="right" data-label="Comissão">
                  <strong>{currency(x.comissao)}</strong>
                </TableCell>
                <TableCell align="right" data-label="%">
                  {Number(x.percentualMedio || 0).toFixed(2)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}

function ProdutosPorVendedorReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.itens || [],
    'vendedor',
    'asc',
  );
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={4}>
          <Metric
            label="Quantidade"
            value={Number(data?.quantidade || 0).toLocaleString('pt-BR')}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <Metric label="Valor" value={currency(data?.valor)} />
        </Grid>
        <Grid item xs={6} md={4}>
          <Metric label="Comissão" value={currency(data?.comissao)} />
        </Grid>
      </Grid>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="vendedor" label="Vendedor" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="quantidade" label="Quantidade" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="desconto" label="Desconto" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="comissao" label="Comissão" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum produto vendido por vendedor no período.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((x) => (
              <TableRow key={`${x.vendedorId}-${x.produtoId}`}>
                <TableCell data-label="Vendedor">{x.vendedor}</TableCell>
                <TableCell data-label="Código">{x.codigo}</TableCell>
                <TableCell data-label="Produto">{x.descricao}</TableCell>
                <TableCell align="right" data-label="Quantidade">
                  {Number(x.quantidade || 0).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell align="right" data-label="Desconto">
                  {currency(x.desconto)}
                </TableCell>
                <TableCell align="right" data-label="Valor">
                  <strong>{currency(x.valor)}</strong>
                </TableCell>
                <TableCell align="right" data-label="Comissão">
                  {currency(x.comissao)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}

function SalesReport({ data, onDetail, onWhatsApp }) {
  const accessors = useMemo(
    () => ({
      data: (x) => Date.parse(x.data) || 0,
      status: (x) => (x.cancelada ? 1 : 0),
    }),
    [],
  );
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.items || [],
    'data',
    'desc',
    accessors,
  );
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}><Metric label="Faturamento" value={currency(data?.faturamento)} /></Grid>
        <Grid item xs={6} md={2.4}><Metric label="Vendas" value={data?.quantidade || 0} /></Grid>
        <Grid item xs={6} md={2.4}><Metric label="Ticket médio" value={currency(data?.ticketMedio)} /></Grid>
        <Grid item xs={6} md={2.4}><Metric label="Descontos" value={currency(data?.descontos)} /></Grid>
        <Grid item xs={6} md={2.4}><Metric label="Canceladas" value={data?.canceladas || 0} color="error.main" /></Grid>
      </Grid>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="data" label="Venda/Data" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="pdv" label="PDV/Operador" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cliente" label="Cliente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="desconto" label="Desconto" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="center">WhatsApp</TableCell>
              <TableCell align="center">Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((x) => (
              <TableRow
                key={x.id}
                hover
                onClick={() => onDetail(x.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell data-label="Venda/data">#{x.id}<br/><small>{new Date(x.data).toLocaleString('pt-BR')}</small></TableCell>
                <TableCell data-label="PDV/operador">{x.pdv}<br/><small>{x.usuario}</small></TableCell>
                <TableCell data-label="Cliente">{x.cliente}</TableCell>
                <TableCell align="right" data-label="Desconto">{currency(x.desconto)}</TableCell>
                <TableCell align="right" data-label="Valor"><strong>{currency(x.valor)}</strong></TableCell>
                <TableCell data-label="Status"><Chip size="small" color={x.cancelada ? 'error' : 'success'} label={x.cancelada ? 'Cancelada' : 'Válida'} /></TableCell>
                <TableCell align="center" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={x.whatsAppEnviado ? `Enviado para ${formatPhone(x.whatsAppTelefone)}` : 'Enviar cupom no WhatsApp'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={x.cancelada}
                        aria-label={`WhatsApp da venda ${x.id}`}
                        onClick={() => onWhatsApp(x.id, x.whatsAppTelefone)}
                        sx={{ color: x.whatsAppEnviado ? '#25D366' : 'text.secondary' }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" data-actions="">
                  <Tooltip title="Ver itens e imprimir">
                    <IconButton size="small" aria-label={`Detalhes da venda ${x.id}`}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}

function PaymentReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(data || [], 'valor', 'desc');
  return (
    <ResponsiveTable>
      <Table size="small">
        <TableHead>
          <TableRow>
            <SortableTh id="descricao" label="Forma" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="transacoes" label="Transações" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="percentual" label="Participação" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((x) => (
            <TableRow key={x.formaPagamentoId}>
              <TableCell data-label="Forma">{x.descricao}</TableCell>
              <TableCell align="right" data-label="Transações">{x.transacoes}</TableCell>
              <TableCell align="right" data-label="Participação">{Number(x.percentual).toFixed(2)}%</TableCell>
              <TableCell align="right" data-label="Valor"><strong>{currency(x.valor)}</strong></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ResponsiveTable>
  );
}

function ProductReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(data || [], 'valor', 'desc');
  return (
    <ResponsiveTable>
      <Table size="small">
        <TableHead>
          <TableRow>
            <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="descricao" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="quantidade" label="Quantidade" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="desconto" label="Desconto" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((x) => (
            <TableRow key={x.produtoId}>
              <TableCell data-label="Código">{x.codigo}</TableCell>
              <TableCell data-label="Produto">{x.descricao}</TableCell>
              <TableCell align="right" data-label="Quantidade">{Number(x.quantidade).toLocaleString('pt-BR')}</TableCell>
              <TableCell align="right" data-label="Desconto">{currency(x.desconto)}</TableCell>
              <TableCell align="right" data-label="Valor"><strong>{currency(x.valor)}</strong></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ResponsiveTable>
  );
}

function convenioFiltrado(data, sinal) {
  const todos = data?.clientes || [];
  const clientes = sinal === 'negativos'
    ? todos.filter((x) => Number(x.saldo) < 0)
    : sinal === 'positivos'
      ? todos.filter((x) => Number(x.saldo) > 0)
      : todos;
  const soma = (chave) => clientes.reduce((total, x) => total + Number(x[chave] || 0), 0);
  return {
    ...(data || {}),
    clientes,
    lancamentos: soma('lancamentos'),
    recebimentosManuais: soma('recebimentosManuais'),
    cashback: soma('cashback'),
    recebimentos: soma('recebimentos'),
    saldo: soma('saldo'),
  };
}

function ConvenioReport({ data }) {
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.clientes || [],
    'cliente',
    'asc',
  );
  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Conta inteira, sem período. Saldo = recebimentos − lançamentos (o mesmo da tela de convênio).
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Metric label="Lançamentos" value={currency(data?.lancamentos)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Metric label="Receb. manuais" value={currency(data?.recebimentosManuais)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Metric label="Cashback" value={currency(data?.cashback)} color="info.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Metric label="Créditos (total)" value={currency(data?.recebimentos)} />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Metric
            label="Saldo"
            value={currency(data?.saldo)}
            color={Number(data?.saldo) < 0 ? 'error.main' : 'success.main'}
          />
        </Grid>
      </Grid>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="cliente" label="Cliente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="limite" label="Limite" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="lancamentos" label="Lançamentos" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="recebimentosManuais" label="Receb. manuais" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cashback" label="Cashback" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="recebimentos" label="Créditos" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="saldo" label="Saldo" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="disponivel" label="Disponível (Saldo + Limite)" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum cliente neste filtro.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((x) => (
              <TableRow key={x.clienteId}>
                <TableCell data-label="Cliente">
                  {x.cliente}{' '}
                  {x.bloqueado && <Chip size="small" color="error" label="Bloqueado" />}
                </TableCell>
                <TableCell align="right" data-label="Limite">
                  {currency(x.limite)}
                </TableCell>
                <TableCell align="right" data-label="Lançamentos">
                  {currency(x.lancamentos)}
                </TableCell>
                <TableCell align="right" data-label="Receb. manuais">
                  {currency(x.recebimentosManuais)}
                </TableCell>
                <TableCell align="right" data-label="Cashback">
                  {currency(x.cashback)}
                </TableCell>
                <TableCell align="right" data-label="Créditos">
                  {currency(x.recebimentos)}
                </TableCell>
                <TableCell
                  align="right"
                  data-label="Saldo"
                  sx={{
                    color: Number(x.saldo) < 0 ? 'error.main' : 'success.main',
                    fontWeight: 700,
                  }}
                >
                  {currency(x.saldo)}
                </TableCell>
                <TableCell align="right" data-label="Disponível (Saldo + Limite)">
                  <strong>{currency(x.disponivel)}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}

function CashbackReport({ data }) {
  const clientesSort = useClientSort(data?.porCliente || [], 'valor', 'desc');
  const itensAccessors = useMemo(
    () => ({ data: (x) => Date.parse(x.data) || 0 }),
    [],
  );
  const itensSort = useClientSort(data?.itens || [], 'data', 'desc', itensAccessors);
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Metric label="Total cashback" value={currency(data?.totalCashback)} color="info.main" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric label="Lançamentos" value={data?.quantidade || 0} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric label="Clientes" value={(data?.porCliente || []).length} />
        </Grid>
      </Grid>

      {(data?.porCliente || []).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Por cliente
          </Typography>
          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableTh id="cliente" label="Cliente" orderBy={clientesSort.orderBy} order={clientesSort.order} onSort={clientesSort.requestSort} />
                  <SortableTh id="quantidade" label="Qtde" align="right" orderBy={clientesSort.orderBy} order={clientesSort.order} onSort={clientesSort.requestSort} />
                  <SortableTh id="valor" label="Valor" align="right" orderBy={clientesSort.orderBy} order={clientesSort.order} onSort={clientesSort.requestSort} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clientesSort.sorted.map((x) => (
                  <TableRow key={x.clienteId}>
                    <TableCell data-label="Cliente">{x.cliente}</TableCell>
                    <TableCell align="right" data-label="Qtde">
                      {x.quantidade}
                    </TableCell>
                    <TableCell align="right" data-label="Valor">
                      <strong>{currency(x.valor)}</strong>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTable>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Detalhe
      </Typography>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="data" label="Data" orderBy={itensSort.orderBy} order={itensSort.order} onSort={itensSort.requestSort} />
              <SortableTh id="cliente" label="Cliente" orderBy={itensSort.orderBy} order={itensSort.order} onSort={itensSort.requestSort} />
              <SortableTh id="vendaId" label="Venda" orderBy={itensSort.orderBy} order={itensSort.order} onSort={itensSort.requestSort} />
              <SortableTh id="observacao" label="Observação" orderBy={itensSort.orderBy} order={itensSort.order} onSort={itensSort.requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={itensSort.orderBy} order={itensSort.order} onSort={itensSort.requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {itensSort.sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum cashback no período.
                </TableCell>
              </TableRow>
            )}
            {itensSort.sorted.map((x) => (
              <TableRow key={x.id}>
                <TableCell data-label="Data">
                  {new Date(x.data).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell data-label="Cliente">{x.cliente}</TableCell>
                <TableCell data-label="Venda">{x.vendaId ? `#${x.vendaId}` : '—'}</TableCell>
                <TableCell data-label="Observação">{x.observacao || '—'}</TableCell>
                <TableCell align="right" data-label="Valor">
                  <strong>{currency(x.valor)}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </>
  );
}
